import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { FormField } from '../ui/FormField';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { apiService } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';

interface QuickAPIImportProps {
  onImport: (apiData: any) => void;
  onCancel: () => void;
}

interface APITemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  config: any;
}

interface QuickImportFormData {
  name?: string;
  description?: string;
  baseUrl?: string;
  requestMethod?: string;
  urlPattern?: string;
  parameterFormat?: string;
  parameterName?: string;
  parameterType?: string;
  searchParameterTypeId?: number;
  organization?: string;
  organizationId?: number;
  authType?: string;
  authHeader?: string;
  authValue?: string;
  price?: number;
  headers?: Record<string, string> | string;
  requestParameters?: Record<string, unknown> | string;
  apiDocumentationUrl?: string;
  countryCodeFormat?: string;
  customCountryFormat?: string;
  isUniversalSearch?: boolean;
  allowRegularUsers?: boolean;
  successCodes?: number[];
  [key: string]: unknown;
}

export const QuickAPIImport: React.FC<QuickAPIImportProps> = ({ onImport, onCancel }) => {
  const { showToast } = useToast();
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [formData, setFormData] = useState<QuickImportFormData>({});
  const [testResult, setTestResult] = useState<any>(null);
  const [testValue, setTestValue] = useState<string>('');
  const [isTesting, setIsTesting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [parameterTypes, setParameterTypes] = useState<any[]>([]);
  const [jsonErrors, setJsonErrors] = useState<{ headers?: string; requestParameters?: string }>({});

  // Pre-defined API templates (fields aligned with Basic & Configuration tab)
  const templates: APITemplate[] = [
    {
      id: 'attestr_vehicle',
      name: 'Attestr Vehicle Registration',
      description: 'Check vehicle registration details using Attestr API',
      category: 'Vehicle',
      config: {
        name: 'Attestr Vehicle Registration Check',
        description: 'Check vehicle registration details using Attestr API',
        baseUrl: 'https://api.attestr.com/api/v2/public/checkx/rc',
        requestMethod: 'POST',
        urlPattern: '/',
        parameterType: 'vehicle_registration',
        organization: 'Attestr',
        authType: 'basic_auth',
        authHeader: 'Authorization',
        authValue: '',
        parameterName: 'reg',
        parameterFormat: 'body_json',
        successCodes: [200, 201],
        price: 5,
        headers: { 'Content-Type': 'application/json' },
        requestParameters: {},
        apiDocumentationUrl: '',
        countryCodeFormat: 'none',
        customCountryFormat: '',
        isUniversalSearch: false,
        allowRegularUsers: false
      }
    },
    {
      id: 'attestr_phone',
      name: 'Attestr Phone Number',
      description: 'Check phone number details using Attestr API',
      category: 'Phone',
      config: {
        name: 'Attestr Phone Number Check',
        description: 'Check phone number details using Attestr API',
        baseUrl: 'https://api.attestr.com/api/v2/public/checkx/phone',
        requestMethod: 'POST',
        urlPattern: '/',
        parameterType: 'phone_number',
        organization: 'Attestr',
        authType: 'basic_auth',
        authHeader: 'Authorization',
        authValue: '',
        parameterName: 'phone',
        parameterFormat: 'body_json',
        successCodes: [200, 201],
        price: 3,
        headers: { 'Content-Type': 'application/json' },
        requestParameters: { phone: '{value}' },
        apiDocumentationUrl: '',
        countryCodeFormat: 'none',
        customCountryFormat: '',
        isUniversalSearch: false,
        allowRegularUsers: false
      }
    },
    {
      id: 'attestr_email',
      name: 'Attestr Email Verification',
      description: 'Verify email address using Attestr API',
      category: 'Email',
      config: {
        name: 'Attestr Email Verification',
        description: 'Verify email address using Attestr API',
        baseUrl: 'https://api.attestr.com/api/v2/public/checkx/email',
        requestMethod: 'POST',
        urlPattern: '/',
        parameterType: 'email',
        organization: 'Attestr',
        authType: 'basic_auth',
        authHeader: 'Authorization',
        authValue: '',
        parameterName: 'email',
        parameterFormat: 'body_json',
        successCodes: [200, 201],
        price: 2,
        headers: { 'Content-Type': 'application/json' },
        requestParameters: { email: '{value}' },
        apiDocumentationUrl: '',
        countryCodeFormat: 'none',
        customCountryFormat: '',
        isUniversalSearch: false,
        allowRegularUsers: false
      }
    },
    {
      id: 'leakcheck_email',
      name: 'LeakCheck Email',
      description: 'LeakCheck v2 email breach lookup',
      category: 'Email',
      config: {
        name: 'LeakCheck Email API',
        description: 'Search email in breach databases via LeakCheck v2',
        baseUrl: 'https://leakcheck.io/api/v2/query',
        requestMethod: 'GET',
        urlPattern: '/{value}',
        parameterType: 'email',
        organization: 'LeakCheck',
        authType: 'api_key',
        authHeader: 'X-API-Key',
        authValue: '',
        parameterName: '',
        parameterFormat: 'url_path',
        successCodes: [200],
        price: 5,
        headers: { 'Accept': 'application/json', 'X-API-Key': 'YOUR_API_KEY' },
        requestParameters: {},
        apiDocumentationUrl: 'https://leakcheck.io/api',
        countryCodeFormat: 'none',
        customCountryFormat: '',
        isUniversalSearch: false,
        allowRegularUsers: false
      }
    },
    {
      id: 'rapidapi_query',
      name: 'RapidAPI GET (query param)',
      description: 'Generic RapidAPI GET with query parameter',
      category: 'Custom',
      config: {
        name: 'RapidAPI Search',
        description: 'RapidAPI GET endpoint with query parameter',
        baseUrl: 'https://example.p.rapidapi.com/search',
        requestMethod: 'GET',
        urlPattern: '/',
        parameterType: 'email',
        organization: 'RapidAPI',
        authType: 'api_key',
        authHeader: 'x-rapidapi-key',
        authValue: '',
        parameterName: 'q',
        parameterFormat: 'query_param',
        successCodes: [200],
        price: 5,
        headers: {
          'x-rapidapi-key': 'YOUR_KEY',
          'x-rapidapi-host': 'example.p.rapidapi.com'
        },
        requestParameters: {},
        apiDocumentationUrl: '',
        countryCodeFormat: 'none',
        customCountryFormat: '',
        isUniversalSearch: false,
        allowRegularUsers: false
      }
    },
    {
      id: 'custom',
      name: 'Custom API',
      description: 'Define your own API configuration',
      category: 'Custom',
      config: {
        name: '',
        description: '',
        baseUrl: '',
        requestMethod: 'GET',
        urlPattern: '/{value}',
        parameterType: '',
        organization: '',
        authType: 'none',
        authHeader: 'Authorization',
        authValue: '',
        parameterName: 'q',
        parameterFormat: 'url_path',
        successCodes: [200, 201],
        price: 0,
        headers: {},
        requestParameters: {},
        apiDocumentationUrl: '',
        countryCodeFormat: 'none',
        customCountryFormat: '',
        isUniversalSearch: false,
        allowRegularUsers: false
      }
    }
  ];

  useEffect(() => {
    loadData();
  }, []);

  // Sync template names to IDs once organizations/parameterTypes load
  useEffect(() => {
    if (!selectedTemplate || (!parameterTypes.length && !organizations.length)) return;
    setFormData(prev => {
      let next = { ...prev };
      if (prev.parameterType && parameterTypes.length && next.searchParameterTypeId == null) {
        const pt = parameterTypes.find(p => p.name === prev.parameterType);
        if (pt) next.searchParameterTypeId = pt.id;
      }
      if (prev.organization && organizations.length && next.organizationId == null) {
        const org = organizations.find(o => o.name === prev.organization);
        if (org) next.organizationId = org.id;
      }
      return next;
    });
  }, [parameterTypes, organizations, selectedTemplate]);

  const loadData = async () => {
    try {
      const [orgs, params] = await Promise.all([
        apiService.getOrganizations(),
        apiService.getSearchParameters()
      ]);
      setOrganizations(orgs);
      setParameterTypes(params);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const suggestedTestValues: Record<string, string> = {
    attestr_vehicle: 'MH12AB1234',
    attestr_phone: '+919876543210',
    attestr_email: 'test@example.com',
    leakcheck_email: 'test@example.com',
    rapidapi_query: 'test@example.com',
    custom: '',
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      const config = { ...template.config };
      if (config.parameterType && parameterTypes.length) {
        const pt = parameterTypes.find(p => p.name === config.parameterType);
        if (pt) config.searchParameterTypeId = pt.id;
      }
      if (config.organization && organizations.length) {
        const org = organizations.find(o => o.name === config.organization);
        if (org) config.organizationId = org.id;
      }
      setFormData(config);
      setTestResult(null);
      setJsonErrors({});
      if (suggestedTestValues[templateId] !== undefined) {
        setTestValue(suggestedTestValues[templateId]);
      }
    }
  };

  const handleTest = async () => {
    if (!formData.baseUrl) {
      showToast('Please enter Base URL before testing', 'error');
      return;
    }
    const valueToSend = testValue.trim() || undefined;
    setIsTesting(true);
    setTestResult(null);
    try {
      const payload = { ...formData, test_value: valueToSend, testValue: valueToSend };
      const result = await apiService.testQuickImport(payload);
      setTestResult(result);
      if (result.success) {
        showToast('API test completed successfully', 'success');
      } else {
        showToast(result.message || 'API test failed. Check configuration.', 'error');
      }
    } catch (error: any) {
      showToast(error.message || 'API test failed', 'error');
      setTestResult({ success: false, error: error.message, message: error.message });
    } finally {
      setIsTesting(false);
    }
  };

  const validateJsonFields = (): boolean => {
    const errs: { headers?: string; requestParameters?: string } = {};
    const headersVal = formData.headers;
    if (typeof headersVal === 'string' && headersVal.trim()) {
      try {
        JSON.parse(headersVal);
      } catch {
        errs.headers = 'Headers must be valid JSON';
      }
    }
    const paramsVal = formData.requestParameters;
    if (typeof paramsVal === 'string' && paramsVal && paramsVal.trim()) {
      try {
        JSON.parse(paramsVal);
      } catch {
        errs.requestParameters = 'Request parameters must be valid JSON';
      }
    }
    setJsonErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const formatJson = (val: unknown): string => {
    if (val == null) return '';
    if (typeof val === 'string') return val;
    return JSON.stringify(val, null, 2);
  };

  const handleJsonChange = (field: 'headers' | 'requestParameters', value: string) => {
    try {
      const parsed = value.trim() ? JSON.parse(value) : {};
      setFormData(prev => ({ ...prev, [field]: parsed }));
      setJsonErrors(prev => ({ ...prev, [field]: undefined }));
    } catch {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleImport = async () => {
    const hasParamType = (formData.searchParameterTypeId != null) ||
      (Boolean(formData.parameterType) && formData.parameterType !== '');
    if (!formData.name || !formData.baseUrl || !formData.urlPattern) {
      showToast('Please fill in name, base URL and URL pattern', 'error');
      return;
    }
    if (!formData.isUniversalSearch && !hasParamType) {
      showToast('Select a parameter type or enable Universal Search', 'error');
      return;
    }
    if (!formData.organizationId && !formData.organization) {
      showToast('Please select an organization', 'error');
      return;
    }
    if (!validateJsonFields()) {
      showToast('Please fix JSON errors in Headers or Request Parameters', 'error');
      return;
    }

    setIsImporting(true);
    try {
      const payload: any = {
        name: formData.name,
        description: formData.description ?? '',
        baseUrl: formData.baseUrl,
        requestMethod: formData.requestMethod ?? 'GET',
        urlPattern: formData.urlPattern ?? '/{value}',
        parameterFormat: formData.parameterFormat ?? 'body_json',
        parameterName: formData.parameterName || 'q',
        requestParameters: typeof formData.requestParameters === 'object' ? formData.requestParameters : {},
        headers: typeof formData.headers === 'object' ? formData.headers : {},
        price: formData.price ?? 0,
        apiDocumentationUrl: formData.apiDocumentationUrl || undefined,
        countryCodeFormat: formData.countryCodeFormat ?? 'none',
        customCountryFormat: formData.customCountryFormat ?? '',
        isUniversalSearch: Boolean(formData.isUniversalSearch),
        allowRegularUsers: Boolean(formData.allowRegularUsers),
      };
      if (formData.organizationId != null) {
        payload.organizationId = Number(formData.organizationId);
      } else if (formData.organization) {
        payload.organization = formData.organization;
      }
      if (formData.searchParameterTypeId != null) {
        payload.searchParameterTypeId = Number(formData.searchParameterTypeId);
      } else if (formData.parameterType) {
        payload.parameterType = formData.parameterType;
      }
      if (formData.authType && formData.authType !== 'none') {
        payload.authType = formData.authType;
        payload.authHeader = formData.authHeader || 'Authorization';
        payload.authValue = formData.authValue || '';
      }
      const result = await apiService.quickImport(payload);
      const apiData = result?.api ?? result;
      showToast(`API "${formData.name}" imported successfully!`, 'success');
      onImport(apiData);
    } catch (error: any) {
      showToast(error.message || 'Import failed', 'error');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Quick API Import</h3>
          
          {/* Template Selection */}
          <FormField label="API Template">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {templates.map(template => (
                <div
                  key={template.id}
                  onClick={() => handleTemplateSelect(template.id)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedTemplate === template.id
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-sm">{template.name}</h4>
                    <Badge variant="outline" className="text-xs">
                      {template.category}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-300">{template.description}</p>
                </div>
              ))}
            </div>
          </FormField>

          {selectedTemplate && (
            <div className="mt-6 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Fields match the Basic &amp; Configuration tab so the imported API is ready to use.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="API Name" required>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter API name"
                  />
                </FormField>

                <FormField label="Base URL" required>
                  <input
                    type="url"
                    value={formData.baseUrl || ''}
                    onChange={(e) => setFormData({...formData, baseUrl: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="https://api.example.com"
                  />
                </FormField>

                <FormField label="URL Pattern" required>
                  <input
                    type="text"
                    value={formData.urlPattern ?? ''}
                    onChange={(e) => setFormData({...formData, urlPattern: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="/search/{value} or /?q={value}"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Use {'{value}'}, {'{phone_number}'}, {'{country_code}'}</p>
                </FormField>

                <FormField label="Request Method">
                  <select
                    value={formData.requestMethod || 'GET'}
                    onChange={(e) => setFormData({...formData, requestMethod: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="DELETE">DELETE</option>
                  </select>
                </FormField>

                <FormField label="Parameter Format">
                  <select
                    value={formData.parameterFormat || 'url_path'}
                    onChange={(e) => setFormData({...formData, parameterFormat: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="url_path">URL Path</option>
                    <option value="query_param">Query Parameter</option>
                    <option value="body_json">JSON Body</option>
                    <option value="form_data">Form Data</option>
                    <option value="custom">Custom Format</option>
                  </select>
                </FormField>

                <FormField label="Parameter Name">
                  <input
                    type="text"
                    value={formData.parameterName || ''}
                    onChange={(e) => setFormData({...formData, parameterName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="q, search, phone, etc."
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">For query_param / body_json / form_data</p>
                </FormField>

                <FormField label={formData.isUniversalSearch ? 'Primary Parameter Type (optional)' : 'Search Parameter Type'}>
                  <select
                    value={formData.searchParameterTypeId ?? formData.parameterType ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        searchParameterTypeId: v ? Number(v) : undefined,
                        parameterType: v ? parameterTypes.find(p => p.id === Number(v))?.name : undefined
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Select parameter type</option>
                    {parameterTypes.map(param => (
                      <option key={param.id} value={param.id}>
                        {param.display_name}
                      </option>
                    ))}
                  </select>
                </FormField>

                <FormField label="Organization" required>
                  <select
                    value={formData.organizationId ?? formData.organization ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        organizationId: v ? Number(v) : undefined,
                        organization: v ? organizations.find(o => o.id === Number(v))?.name : undefined
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Select organization</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </FormField>

                <FormField label="Price (Int Coins)">
                  <input
                    type="number"
                    value={formData.price ?? 0}
                    onChange={(e) => setFormData({...formData, price: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    min="0"
                  />
                </FormField>

                <FormField label="API Documentation URL">
                  <input
                    type="url"
                    value={formData.apiDocumentationUrl ?? ''}
                    onChange={(e) => setFormData({...formData, apiDocumentationUrl: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="https://docs.example.com/api"
                  />
                </FormField>

                <FormField label="Auth Type">
                  <select
                    value={formData.authType || 'none'}
                    onChange={(e) => setFormData({...formData, authType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="none">No Auth</option>
                    <option value="api_key">API Key</option>
                    <option value="bearer_token">Bearer Token</option>
                    <option value="basic_auth">Basic Auth</option>
                  </select>
                </FormField>

                <FormField label="Country Code Format">
                  <select
                    value={formData.countryCodeFormat ?? 'none'}
                    onChange={(e) => setFormData({...formData, countryCodeFormat: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="none">No Country Code</option>
                    <option value="numeric">Numeric (91)</option>
                    <option value="plus_prefix">Plus Prefix (+91)</option>
                    <option value="iso_code">ISO Code (IN)</option>
                    <option value="custom">Custom Format</option>
                  </select>
                </FormField>
              </div>

              {formData.countryCodeFormat === 'custom' && (
                <FormField label="Custom Country Format">
                  <input
                    type="text"
                    value={formData.customCountryFormat ?? ''}
                    onChange={(e) => setFormData({...formData, customCountryFormat: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="+{country_code}, etc."
                  />
                </FormField>
              )}

              <div className="flex flex-wrap gap-6">
                <FormField label="">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(formData.isUniversalSearch)}
                      onChange={(e) => setFormData({...formData, isUniversalSearch: e.target.checked})}
                      className="h-4 w-4 text-indigo-600 dark:text-indigo-400 rounded border-gray-300 dark:border-gray-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Universal Search (works with all parameter types)</span>
                  </label>
                </FormField>
                <FormField label="">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(formData.allowRegularUsers)}
                      onChange={(e) => setFormData({...formData, allowRegularUsers: e.target.checked})}
                      className="h-4 w-4 text-indigo-600 dark:text-indigo-400 rounded border-gray-300 dark:border-gray-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">Allow Regular Users</span>
                  </label>
                </FormField>
              </div>

              <FormField label="Description">
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  rows={2}
                  placeholder="Brief description of what this API does..."
                />
              </FormField>

              {formData.authType !== 'none' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Auth Header">
                    <input
                      type="text"
                      value={formData.authHeader || 'Authorization'}
                      onChange={(e) => setFormData({...formData, authHeader: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </FormField>
                  <FormField label="Auth Value">
                    <input
                      type="password"
                      value={formData.authValue || ''}
                      onChange={(e) => setFormData({...formData, authValue: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Token or key value"
                    />
                  </FormField>
                </div>
              )}

              <FormField label="Headers (JSON)">
                <textarea
                  value={formatJson(formData.headers)}
                  onChange={(e) => handleJsonChange('headers', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md font-mono text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${jsonErrors.headers ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                  rows={3}
                  placeholder='{"Content-Type": "application/json"}'
                />
                {jsonErrors.headers && <p className="text-sm text-red-600 dark:text-red-400 mt-1">{jsonErrors.headers}</p>}
              </FormField>

              <FormField label="Request Parameters (JSON)">
                <textarea
                  value={formatJson(formData.requestParameters)}
                  onChange={(e) => handleJsonChange('requestParameters', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md font-mono text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${jsonErrors.requestParameters ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                  rows={3}
                  placeholder='{"api_key": "..."}'
                />
                {jsonErrors.requestParameters && <p className="text-sm text-red-600 dark:text-red-400 mt-1">{jsonErrors.requestParameters}</p>}
              </FormField>

              {/* Test API section */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/50 space-y-4">
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Test API</h4>
                <p className="text-xs text-gray-600 dark:text-gray-300">
                  Enter a value to send to the API (e.g. phone number, UPI ID, vehicle number). The value replaces {'{value}'} in URL/body.
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <FormField label="Test value" className="flex-1 min-w-[200px]">
                    <input
                      type="text"
                      value={testValue}
                      onChange={(e) => setTestValue(e.target.value)}
                      placeholder="e.g. 9000000000@upi, +919876543210, MH12AB1234"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    />
                  </FormField>
                  <Button
                    onClick={handleTest}
                    disabled={isTesting || !formData.baseUrl}
                    variant="outline"
                    className="shrink-0"
                  >
                    {isTesting ? 'Testing...' : 'Test API'}
                  </Button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-2">
                <Button
                  onClick={handleImport}
                  disabled={isImporting || !formData.name || !formData.baseUrl || !formData.urlPattern || (!formData.isUniversalSearch && !formData.searchParameterTypeId && !formData.parameterType) || (!formData.organizationId && !formData.organization)}
                  variant="primary"
                >
                  {isImporting ? 'Importing...' : 'Import API'}
                </Button>
                <Button variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
              </div>

              {/* Test Results */}
              {testResult && (
                <div className="mt-4 p-4 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <span className={testResult.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                      {testResult.success ? '✅' : '❌'}
                    </span>
                    Test Result
                    {testResult.test_result?.response_time != null && (
                      <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                        ({testResult.test_result.response_time.toFixed(2)}s)
                      </span>
                    )}
                  </h4>
                  <div className="text-sm space-y-1">
                    <p><strong>Status:</strong> {testResult.success ? 'Success' : 'Failed'}</p>
                    {testResult.test_result?.response_status != null && (
                      <p><strong>HTTP Status:</strong> {testResult.test_result.response_status}</p>
                    )}
                    {(testResult.test_result?.error || testResult.error || testResult.message) && (
                      <p className="text-red-600 dark:text-red-400">
                        <strong>Error:</strong> {testResult.test_result?.error || testResult.error || testResult.message}
                      </p>
                    )}
                  </div>
                  {testResult.test_result?.request && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-200">Request</summary>
                      <pre className="text-xs bg-gray-50 dark:bg-gray-900 border rounded p-2 mt-2 overflow-auto max-h-40">
                        {JSON.stringify(testResult.test_result.request, null, 2)}
                      </pre>
                    </details>
                  )}
                  {testResult.test_result?.response != null && (
                    <details className="mt-2" open={!testResult.success}>
                      <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-200">Response</summary>
                      <pre className="text-xs bg-gray-50 dark:bg-gray-900 border rounded p-2 mt-2 overflow-auto max-h-48">
                        {typeof testResult.test_result.response === 'object'
                          ? JSON.stringify(testResult.test_result.response, null, 2)
                          : String(testResult.test_result.response)}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
